import React, { useState } from "react";
import { Link } from "react-router-dom";

// Modal component for deleting a unit
function DeleteUnitModal({ open, onClose, onDelete, unit }) {
  if (!open || !unit) return null;

  return (
    <div className="z-50 fixed inset-0 flex justify-center items-center bg-black bg-opacity-50">
      <div className="bg-white shadow-lg p-6 rounded-lg w-full max-w-sm">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold text-lg">Delete Unit</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            &times;
          </button>
        </div>

        <div className="mb-6">
          Do you want to delete the Unit{" "}
          <span className="font-semibold">{unit.house}</span>?
        </div>

        <div className="flex justify-end gap-2">
          <button
            className="bg-green-600 px-4 py-2 rounded text-white"
            onClick={onClose}
          >
            No
          </button>
          <button
            className="bg-red-600 px-4 py-2 rounded text-white"
            onClick={() => onDelete(unit)}
          >
            Yes
          </button>
        </div>
      </div>
    </div>
  );
}

export default function UnitTableView({
  units = [],
  houseFilters,
  onDelete,
  roundUp,
}) {
  // Pagination setup
  const pageSize = 12;
  const [currentPage, setCurrentPage] = useState(0);

  // Modal states
  const [showModalDeleteHouse, setShowModalDeleteHouse] = useState(false);
  const [modalDeleteUnit, setModalDeleteUnit] = useState(null);

  // FIX: Assign units (the already filtered array from the parent) to filteredUnits
  const filteredUnits = units;

  // Pagination
  const startIndex = currentPage * pageSize;
  const endIndex = Math.min(startIndex + pageSize, filteredUnits.length);
  const pagedUnits = filteredUnits.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredUnits.length / pageSize);

  // Pagination controls
  const handlePrevPage = () => setCurrentPage((p) => Math.max(p - 1, 0));
  const handleNextPage = () => setCurrentPage((p) => Math.min(p + 1, totalPages - 1));

  // Modal handlers
  function handleOpenModalDeleteHouse(unit) {
    setModalDeleteUnit(unit);
    setShowModalDeleteHouse(true);
  }

  function handleCloseModalDeleteHouse() {
    setShowModalDeleteHouse(false);
    setModalDeleteUnit(null);
  }

  function handleDeleteHouse(unit) {
    onDelete(unit);
    setShowModalDeleteHouse(false);
    setModalDeleteUnit(null);
  }

  // CSV Export utility - FIXED to use property_name instead of apartment
  function exportToCSV(data) {
    const replacer = (key, value) => (value === null ? "" : value);
    const header = [
      "house",
      "property_name",
      "tenant",
      "status",
      "house_rent",
      "effective_opening_balance",
      "legacy_house_opening_balance",
      "opening_balance_source",
      "rent_payment_status",
    ];
    const csv = [
      header.join(","),
      ...data.map((row) =>
        header.map((field) => JSON.stringify(row[field], replacer)).join(",")
      ),
    ].join("\r\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "units.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  }

  return (
    <div>
      <div className="bg-white shadow-md p-4 border border-gray-100 rounded-lg">
        {/* Search + Export Controls */}
        <div className="flex justify-end gap-2 mb-4">
          <button
            className="bg-green-600 hover:bg-green-700 px-4 py-1 rounded font-semibold text-md text-white"
            onClick={() => exportToCSV(filteredUnits)}
          >
            Export CSV
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="bg-white min-w-full">
            <thead>
              <tr>
                <th className="p-2 border-b font-bold text-gray-700 text-md text-left">Image</th>
                <th className="p-2 border-b font-bold text-gray-700 text-md text-left">House</th>
                <th className="p-2 border-b font-bold text-gray-700 text-md text-left">Property</th>
                <th className="p-2 border-b font-bold text-gray-700 text-md text-left">Tenant</th>
                <th className="p-2 border-b font-bold text-gray-700 text-md text-left">Status</th>
                <th className="p-2 border-b font-bold text-gray-700 text-md text-left">Rent</th>
                <th className="p-2 border-b font-bold text-gray-700 text-md text-left">Opening Balance</th>
                <th className="p-2 border-b font-bold text-gray-700 text-md text-left">Payment Status</th>
                <th className="p-2 border-b font-bold text-gray-700 text-md text-left">Actions</th>
              </tr>
            </thead>

            <tbody>
              {pagedUnits.length === 0 ? (
                <tr>
                  <td colSpan="8" className="p-8 text-center text-gray-500">
                    No units found
                  </td>
                </tr>
              ) : (
                pagedUnits.map((unit, i) => (
                  <tr key={unit.house_id || i} className="hover:bg-gray-50">
                    <td className="p-2 border-b">
                      <img
                        src={unit.house_image}
                        alt="img"
                        className="rounded w-20 h-12 object-cover"
                      />
                    </td>

                    <td className="p-2 border-b">
                      <Link
                        to={`/selected-house/${unit.house_id}`}
                        className="text-blue-600 hover:underline"
                      >
                        {unit.house}
                      </Link>
                    </td>

                    <td className="p-2 border-b">
                      {unit.property_name || unit.apartment || '-'}
                    </td>

                    <td className="p-2 border-b capitalize">
                      {unit.tenant ? unit.tenant.toLowerCase() : '-'}
                    </td>

                    <td className="p-2 border-b">
                      {unit.status === "vacant" ? (
                        <span className="bg-green-600 p-2 rounded-lg text-white text-sm">
                          Vacant
                        </span>
                      ) : (
                        <span className="bg-yellow-500 p-2 rounded-lg text-white text-sm">
                          Occupied
                        </span>
                      )}
                    </td>

                    <td className="p-2 border-b">
                      {unit.house_rent === 0
                        ? `Ksh. ${roundUp(unit.service_charge)?.toLocaleString()} `
                        : unit.house_rent > 0 && unit.booking_value > 0
                        ? `Ksh. ${roundUp(
                            (unit.house_rent || 0) * parseInt(unit.rent_period || 1)
                          )?.toLocaleString()} /${unit.rent_period_name}`
                        : unit.house_rent > 0
                        ? `Ksh. ${roundUp(unit.house_rent)?.toLocaleString()} /month`
                        : ""}
                    </td>

                    <td className="p-2 border-b">
                      {`Ksh. ${Number(
                        unit.effective_opening_balance || 0
                      ).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`}
                    </td>

                    <td className="p-2 border-b">
                      {unit.rent_payment_status === "paid" ? (
                        <span className="bg-green-500 p-2 rounded-lg text-white text-sm">
                          Paid
                        </span>
                      ) : unit.rent_payment_status === "partial" ? (
                        <span className="bg-gray-400 p-2 rounded-lg text-white text-sm">
                          Partial
                        </span>
                      ) : unit.rent_payment_status === "unpaid" ? (
                        <span className="bg-red-500 p-2 rounded-lg text-white text-sm">
                          Unpaid
                        </span>
                      ) : (
                        ""
                      )}
                    </td>

                    <td className="p-2 border-b">
                      <div className="flex gap-2">
                        {/* Delete Button */}
                        <button
                          onClick={() => handleOpenModalDeleteHouse(unit)}
                          className="bg-red-600 hover:bg-red-700 p-2 rounded-lg text-white"
                          title="Delete Unit"
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex justify-center items-center gap-4 py-8">
          <button
            onClick={handlePrevPage}
            disabled={currentPage === 0}
            className="bg-gray-100 disabled:opacity-50 px-3 py-1 border rounded text-gray-700"
          >
            &lt;
          </button>
          <span className="text-gray-700 text-sm">{`${startIndex + 1}-${endIndex} of ${filteredUnits.length}`}</span>
          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages - 1 || totalPages === 0}
            className="bg-gray-100 disabled:opacity-50 px-3 py-1 border rounded text-gray-700"
          >
            &gt;
          </button>
        </div>
      </div>

      {/* Modals */}
      <DeleteUnitModal
        open={showModalDeleteHouse}
        onClose={handleCloseModalDeleteHouse}
        onDelete={handleDeleteHouse}
        unit={modalDeleteUnit}
      />
    </div>
  );
}
